import { useEffect, useState, useRef, type CSSProperties } from "react";
import type { TableSetupAlert } from "../../types/intercom";
import { waitlistSetupList, waitlistSetupAction } from "../../lib/api";
import { playSpeakeasyChime, isAudioEnabled, setAudioEnabled } from "../../lib/audio";

const ROOM_LABELS: Record<string, string> = {
  lounge: "Lounge",
  library: "Library",
  parlor: "Parlor",
  any: "Any Room",
};

const ROOM_COLORS: Record<string, string> = {
  lounge: "#7EB8F7",
  library: "#7EC8A4",
  parlor: "#D49BC4",
  any: "var(--text, #f4f4f5)",
};

interface Props {
  currentRoom?: string | null; // Optional: filter alerts if the tablet is station-bound
}

export default function TableSetupAlertBanner({ currentRoom }: Props) {
  const [alerts, setAlerts] = useState<TableSetupAlert[]>([]);
  const [soundOn, setSoundOn] = useState(isAudioEnabled());

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const locks = useRef(new Set<string>());
  const mounted = useRef(true);
  const revision = useRef(0);
  const previousStatuses = useRef(new Map<string, string>());
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    let pending = false;
    const poll = async () => {
      if (pending) return;
      pending = true;
      const startedRevision = revision.current;
      try {
        const { alerts: rows } = await waitlistSetupList();
        if (cancelled || startedRevision !== revision.current || locks.current.size) return;
        const scoped = rows.filter(alert => !currentRoom || alert.room_id === "any" || alert.room_id === currentRoom);
        if (scoped.some(alert => previousStatuses.current.get(alert.id) !== alert.status)) playSpeakeasyChime("message");
        previousStatuses.current = new Map(scoped.map(alert => [alert.id, alert.status]));
        setAlerts(scoped);
        setError(null);
      } catch {
        if (!cancelled) setError("Table setup status is stale - retrying.");
      } finally { pending = false; }
    };
    void poll();
    const timer = setInterval(poll, 10000);
    return () => { cancelled = true; mounted.current = false; clearInterval(timer); };
  }, [currentRoom]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setAudioEnabled(next);
  };

  const updateAlert = async (alert: TableSetupAlert, action: "server_alerted" | "table_ready" | "dismiss") => {
    if (action !== "dismiss" && !alert.table_id) {
      setError("Setup needs a held table. Ask the host to assign a table and request setup again.");
      return;
    }
    const entryId = alert.source_id || alert.id;
    if (locks.current.has(entryId)) return;
    locks.current.add(entryId);
    ++revision.current;
    setBusy(previous => ({ ...previous, [alert.id]: true }));
    try {
      const result = await waitlistSetupAction(action, entryId, alert.table_id);
      if (!mounted.current) return;
      setAlerts(previous => action === "dismiss" ? previous.filter(row => row.id !== alert.id) : previous.map(row => row.id === alert.id ? result.alert : row));
      setError(null);
    } catch (failure) {
      if (mounted.current) setError((failure as Error).message || "Could not save table setup status.");
    } finally {
      ++revision.current;
      locks.current.delete(entryId);
      if (mounted.current) setBusy(previous => ({ ...previous, [alert.id]: false }));
    }
  };

  const visibleAlerts = alerts.filter(alert => !currentRoom || alert.room_id === "any" || alert.room_id === currentRoom);
  if (visibleAlerts.length === 0 && !error) return null;

  return (
    <div style={styles.container}>
      {error && <p role="alert" style={{ background: "#241818", color: "#ffb6ac", padding: 12 }}>{error}</p>}
      {visibleAlerts.map((alert) => {
        const roomColor = ROOM_COLORS[alert.room_id] || "var(--gold)";
        const roomName = ROOM_LABELS[alert.room_id] || alert.room_id;

        return (
          <div
            key={alert.id}
            style={{
              ...styles.bannerCard,
              borderLeft: `4px solid ${roomColor}`,
            }}
          >
            <div style={styles.iconCircle}>🛎️</div>

            <div style={styles.content}>
              <div style={styles.headerRow}>
                <span style={{ ...styles.roomPill, color: roomColor, borderColor: `${roomColor}66` }}>
                  {roomName} {alert.table_label ? `· ${alert.table_label}` : ""}
                </span>
                <span style={styles.partyTitle}>
                  {alert.party_name} · Party of {alert.party_size}
                </span>
                {alert.intent && (
                  <span style={styles.intentPill}>
                    {alert.intent}
                  </span>
                )}
              </div>

              <div style={styles.statusRow}>
                {!alert.table_id && <span role="alert" style={styles.statusRequested}>Setup needs a held table — ask the host to assign it.</span>}
                <span style={styles.arrivalText}>
                  Setup requested: {new Date(alert.arrived_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </span>
                {alert.status === "setup_requested" && (
                  <span style={styles.statusRequested}>⚠️ Table Setup Needed</span>
                )}
                {alert.status === "server_alerted" && alert.table_id && (
                  <span style={styles.statusAlerted}>🟡 Server Setting Table...</span>
                )}
                {alert.status === "table_ready" && alert.table_id && (
                  <span style={styles.statusReady}>🟢 Table Ready for Guests!</span>
                )}
              </div>
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.soundToggle}
                onClick={toggleSound}
                title={soundOn ? "Mute audio chimes" : "Unmute audio chimes"}
              >
                {soundOn ? "🔔" : "🔕"}
              </button>

              {alert.status === "setup_requested" && (
                <button
                  type="button"
                  style={styles.alertServerBtn}
                  disabled={busy[alert.id] || !alert.table_id}
                  onClick={() => updateAlert(alert, "server_alerted")}
                >
                  🔔 Alert Server
                </button>
              )}

              {alert.status !== "table_ready" || !alert.table_id ? (
                <button
                  type="button"
                  style={styles.tableReadyBtn}
                  disabled={busy[alert.id] || !alert.table_id}
                  onClick={() => updateAlert(alert, "table_ready")}
                >
                  ✓ Table Ready
                </button>
              ) : (
                <span style={styles.readyBadge}>✓ Setup complete</span>
              )}

              <button
                type="button"
                style={styles.dismissBtn}
                disabled={busy[alert.id]}
                onClick={() => updateAlert(alert, "dismiss")}
                title="Dismiss alert"
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    position: "fixed",
    top: 12,
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(96vw, 840px)",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    pointerEvents: "auto",
  },
  bannerCard: {
    background: "rgba(18, 18, 20, 0.96)",
    backdropFilter: "blur(12px)",
    border: "1px solid var(--border, #27272a)",
    borderRadius: 12,
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.65), 0 0 16px rgba(249, 175, 54, 0.15)",
    animation: "slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  iconCircle: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "rgba(249, 175, 54, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    flexShrink: 0,
  },
  content: {
    flex: "1 1 200px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  roomPill: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "2px 8px",
    borderRadius: 6,
    border: "1px solid currentColor",
    background: "rgba(255, 255, 255, 0.04)",
  },
  partyTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "var(--text, #f4f4f5)",
  },
  intentPill: {
    fontSize: 11,
    color: "var(--gold, #F9AF36)",
    background: "rgba(249, 175, 54, 0.1)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    padding: "2px 6px",
    borderRadius: 4,
    fontWeight: 600,
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    fontSize: 12,
  },
  arrivalText: {
    color: "var(--muted, #a1a1aa)",
  },
  statusRequested: {
    color: "#E05040",
    fontWeight: 700,
  },
  statusAlerted: {
    color: "#F9AF36",
    fontWeight: 700,
  },
  statusReady: {
    color: "#27ae60",
    fontWeight: 700,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  soundToggle: {
    background: "transparent",
    border: "1px solid var(--border, #27272a)",
    color: "var(--text, #f4f4f5)",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    cursor: "pointer",
  },
  alertServerBtn: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  tableReadyBtn: {
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#000",
    borderRadius: 8,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  readyBadge: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid #27ae60",
    color: "#27ae60",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 700,
  },
  dismissBtn: {
    background: "transparent",
    border: "1px solid var(--border, #27272a)",
    color: "var(--muted, #a1a1aa)",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
};
